/**
 * Reports API
 * Domain API via HTTP transport (apiTransport) to the backend /api RPC.
 */

import { Report, ReportType, ScheduledReportConfig, CustomReportConfig } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

// Report delete, scheduled/custom CRUD, export, custom generate: HOLLOW stubs where not yet implemented.
export const reportsApi = {
  generate: async (reportType: ReportType, parameters: Record<string, unknown>, name?: string) => {
    const context = getContext();
    return router.post<Report>('/reports/generate', { type: reportType, parameters, name }, context);
  },
  waitForReportCompletion: async (reportId: string, maxAttempts = 10, interval = 500): Promise<Report> => {
    for (let i = 0; i < maxAttempts; i++) {
      const report = await reportsApi.get(reportId);
      if (report.status === 'Completed') return report;
      if (report.status === 'Failed') throw new Error('Report generation failed');
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    throw new Error('Report generation timed out');
  },
  generateAndWait: async (reportType: ReportType, parameters: Record<string, unknown>, name?: string): Promise<Report> => {
    const report = await reportsApi.generate(reportType, parameters, name);
    return reportsApi.waitForReportCompletion(report.id);
  },
  list: async () => router.get<Report[]>('/reports'),
  get: async (id: string) => router.get<Report>(`/reports/${id}`),
  delete: async (id: string) => {
    const context = getContext();
    return router.delete<{ success: boolean }>(`/reports/${id}`, context);
  },
  scheduled: {
    list: async () => router.get<Report[]>('/reports/scheduled'),
    create: async (config: ScheduledReportConfig) => {
      const context = getContext();
      return router.post<Report>('/reports/scheduled', config, context);
    },
    get: async (id: string) => router.get<Report>(`/reports/scheduled/${id}`),
    update: async (id: string, updates: Partial<ScheduledReportConfig>) => router.patch<Report>(`/reports/scheduled/${id}`, updates),
    delete: async (id: string) => router.delete<{ success: boolean }>(`/reports/scheduled/${id}`),
    execute: async (id: string) => router.post<Report>(`/reports/scheduled/${id}/execute`, {})
  },
  custom: {
    list: async () => router.get<Report[]>('/reports/custom'),
    create: async (config: CustomReportConfig) => router.post<Report>('/reports/custom', config),
    get: async (id: string) => router.get<Report>(`/reports/custom/${id}`),
    update: async (id: string, updates: Partial<CustomReportConfig>) => router.patch<Report>(`/reports/custom/${id}`, updates),
    delete: async (id: string) => router.delete<{ success: boolean }>(`/reports/custom/${id}`),
    export: async (id: string, format: string) => router.get<Blob>(`/reports/custom/${id}/export?format=${format}`),
    generate: async (id: string) => router.post<{ success: boolean }>(`/reports/custom/${id}/generate`, {})
  },
  export: async (id: string, format: string) => router.get<Blob>(`/reports/${id}/export?format=${format}`)
};

